// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

import { unwrap } from "@granite/api-client";
import { Button, Spinner } from "@granite/ui";
import { useQuery } from "@tanstack/react-query";
import { getRouteApi, Link } from "@tanstack/react-router";

import { AuthLayout, FormAlert, linkClass } from "../components/AuthLayout.js";
import { errorMessage } from "../lib/errors.js";
import { t } from "../lib/i18n.js";
import { authClient } from "../lib/session.js";

const route = getRouteApi("/verify-email");

/** Opened from the confirmation email. The token works once, so the request is never repeated. */
export function VerifyEmailPage() {
  const { token } = route.useSearch();
  const verify = useQuery({
    queryKey: ["verify-email", token],
    queryFn: async () => {
      unwrap(await authClient.POST("/v1/auth/verify-email", { body: { token: token ?? "" } }));
      return true;
    },
    enabled: Boolean(token),
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return (
    <AuthLayout title={t("verify.title")}>
      {!token ? (
        <FailedVerification message={t("verify.missingToken")} />
      ) : verify.isPending ? (
        <p role="status" className="flex items-center gap-2 text-sm text-fg-muted">
          <Spinner size="sm" />
          {t("verify.pending")}
        </p>
      ) : verify.isError ? (
        <FailedVerification message={errorMessage(verify.error)} />
      ) : (
        <>
          <FormAlert tone="success">{t("verify.done")}</FormAlert>
          <Button asChild>
            <Link to="/login">{t("common.signIn")}</Link>
          </Button>
        </>
      )}
    </AuthLayout>
  );
}

function FailedVerification({ message }: { message: string }) {
  return (
    <>
      <FormAlert tone="error">{message}</FormAlert>
      <div className="flex flex-col items-start gap-2 text-sm">
        <Link to="/check-email" className={linkClass}>
          {t("verify.requestNew")}
        </Link>
        <Link to="/login" className={linkClass}>
          {t("common.backToSignIn")}
        </Link>
      </div>
    </>
  );
}
